import { RangeSettings, UserGoalsData } from "../types"
import { SCORE_RANGES_TYPE } from "../constants/goals"
import { EACT_SCORING } from "../constants/scores"
import { ProgramAndCoursesInfo } from "../../../types/gqlTypes/graphql"
import { statusHeadings } from "../components/ScoreWidget/helpers"
import { ColumnFormatterContextType } from "../components/Table"

export const mapScoreRangesWithTests = (
  ranges: RangeSettings[],
  goals: UserGoalsData[],
  programsAndCourses: ProgramAndCoursesInfo[]
) => {
  if (!ranges || !goals) return undefined

  const filteredRanges = ranges.filter(
    range =>
      range["test_view_score_ranges.metricType"] === SCORE_RANGES_TYPE.scaled ||
      range["test_view_score_ranges.metricType"] === SCORE_RANGES_TYPE.overall
  )

  return goals.reduce((res, goal: UserGoalsData) => {
    const availableProgram = programsAndCourses.find(
      item => item.program === goal.test
    )

    if (!availableProgram) return res

    const rangeId = `${goal.test.toUpperCase()}${
      goal.subtest ? `::${goal.subtest}` : ""
    }`

    const rangeValue = filteredRanges.find(
      el => el["test_view_score_ranges.rangeSetId"] === rangeId
    )

    if (!rangeValue) return res

    return [
      ...res,
      {
        ...goal,
        testDisplayName: availableProgram.programInfo.displayName || goal.test,
        visible: !!rangeValue,
        minRange: rangeValue
          ? Number(rangeValue["test_view_score_ranges.rangeMin"])
          : 0,
        maxRange: rangeValue
          ? Number(rangeValue["test_view_score_ranges.rangeMax"])
          : 100
      }
    ]
  }, [])
}

export const sortGoalsData = (goals: UserGoalsData[]) => {
  return goals.sort((item1, item2) => {
    const testCompare = item1.test.localeCompare(item2.test)
    if (testCompare !== 0) return testCompare

    if (item1.subtest === null && item2.subtest !== null) return -1
    if (item1.subtest !== null && item2.subtest === null) return 1
    if (item1.subtest === null && item2.subtest === null) return 0

    return item1.subtest.localeCompare(item2.subtest)
  })
}

export const hamiltonMethodForPercentageCalc = (percents: number[]) => {
  if (percents.length === 0) return []

  const initialPercentsSum = percents.reduce((res, percent) => res + percent, 0)

  if (initialPercentsSum === 0) {
    return percents
  }

  const normalized =
    initialPercentsSum > 100
      ? percents.map(percent =>
          Number(((percent / initialPercentsSum) * 100).toFixed(1))
        )
      : [...percents]

  const percentsObject = normalized.map((value, originalIndex) => ({
    originalIndex,
    value,
    floor: Math.floor(value),
    remainder: value % 1
  }))

  const totalFloored = percentsObject.reduce((sum, item) => sum + item.floor, 0)

  const percentsToAdd = 100 - totalFloored

  if (percentsToAdd > percents.length) {
    return percents
  }

  const withAdditions = percentsObject
    .map(item => ({ ...item }))
    .sort(
      (a, b) => b.remainder - a.remainder || a.originalIndex - b.originalIndex
    )
    .map((item, i) => ({
      ...item,
      resultValue: item.floor + (i < percentsToAdd ? 1 : 0)
    }))

  return percentsObject.map(item => {
    const modified = withAdditions.find(
      x => x.originalIndex === item.originalIndex
    )
    return modified ? modified.resultValue : item.floor
  })
}

export const getEACTScores = (score: number | string | null | undefined) => {
  // A blank string is a missing score (the views API serializes SQL NULL as
  // ""); without this guard Number("") === 0 maps it to the lowest band.
  if (
    score === null ||
    score === undefined ||
    (typeof score === "string" && score.trim() === "")
  )
    return "--"

  const scoreValue = Number(score)

  if (scoreValue <= Number(EACT_SCORING[0].max)) {
    return EACT_SCORING[0].label
  }

  const scoring = EACT_SCORING.find(
    item => scoreValue >= Number(item.min) && scoreValue <= Number(item.max)
  )

  return !!scoring ? scoring.label : score
}

export const isScoreBearingActivity = (activityType?: string) => {
  if (!activityType) return true

  return ["finish", "complete"].includes(activityType.toLowerCase())
}

export const isBooleanLikeTrue = (value: unknown): boolean =>
  value === true || value === "true"

export const scoresColumnFormatter = (
  program: string,
  subject: string,
  isDiagnostic: boolean,
  score: number | string | null | undefined,
  context: ColumnFormatterContextType,
  activityType?: string
) => {
  // The views API serializes SQL NULL as "" (non-nullable String on the
  // wire), so a blank string means no score, not zero.
  const isBlankScore =
    score === null ||
    score === undefined ||
    (typeof score === "string" && score.trim() === "")

  const adjustedScore =
    !isBlankScore && isScoreBearingActivity(activityType) ? score : null

  // Only apply EACT scoring ranges for ACT-related diagnostic tests
  if (
    program &&
    !!isDiagnostic &&
    (program.toUpperCase() === "ACT" || program.toUpperCase() === "EACT")
  ) {
    return getEACTScores(adjustedScore)
  }

  const programData = context?.programsAndCourses?.find(
    item => item?.program === program
  )

  const isEOCProgram = programData?.programInfo?.testType === "EOC"

  if (isEOCProgram) {
    const benchmarkData = statusHeadings({
      availableGoals: context?.goals,
      averageScore: adjustedScore === null ? null : Number(adjustedScore),
      program,
      course: {
        name: subject
      },
      isFinishingScore: activityType === "finish"
    })

    return (
      context?.handlers?.eocValueRender?.(benchmarkData) ||
      benchmarkData?.value ||
      "--"
    )
  }

  return adjustedScore ?? "--"
}

export const getGoalsNotDiagnosticFilter = (requestViewName: string) => {
  return [
    {
      member: `${requestViewName}.isDiagnosticForACT`,
      operator: "equals",
      values: ["false"]
    }
  ]
}
